import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Stack, Typography, Paper, Divider, Chip, Button, IconButton, Tooltip,
    Accordion, AccordionSummary, AccordionDetails,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Alert, FormControlLabel, Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CreateApi from '../../../shared/Settings/appSettings';
import { StyledTextboxInput } from '../../../shared/Components/StyledTextboxInput';
import { StyledAutocompleteInput } from '../../../shared/Components/StyledAutocompleteInput';
import { useTBContext } from '../../Contexts/useTBStates';
import { useAuth } from 'react-oidc-context';
import type { UserRewards, TourRewardBucket, RewardBucket, RewardTransaction, RewardTransactionType, AddRewardPayload, UpdateRewardExpiresPayload } from '@moment-golf/types';

interface RewardsSectionProps {
    appDims: any;
    compHeights: any;
}

const EMPTY_MG_BUCKET: RewardBucket = {
    currentBalance: 0,
    lifetimeEarned: 0,
    lifetimeRedeemed: 0,
    transactions: [],
};

const formatDateUTC = (val: unknown) => {
    if (!val) return '—';
    try { return new Date(val as string).toLocaleDateString('en-US', { timeZone: 'UTC' }); } catch { return '—'; }
};

const toUTCDateInputValue = (val: unknown) => {
    if (!val) return '';
    try { return new Date(val as string).toISOString().slice(0, 10); } catch { return ''; }
};

// ── Stat chip ────────────────────────────────────────────────────────────────
const StatChip = ({ label, value, color, factor }: { label: string; value: number | string; color?: 'primary' | 'success' | 'default'; factor: number }) => (
    <Stack direction="column" alignItems="center" spacing={0.25}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 * factor, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
        </Typography>
        <Chip
            label={value}
            size="small"
            color={color ?? 'default'}
            sx={{ fontSize: 12 * factor, fontWeight: 700 }}
        />
    </Stack>
);

// ── Transaction log table ────────────────────────────────────────────────────
const TransactionLog = ({
    transactions,
    factor,
    onEditExpires,
}: {
    transactions: RewardTransaction[];
    factor: number;
    onEditExpires: (tx: RewardTransaction) => void;
}) => {
    if (!transactions.length) {
        return (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1, fontSize: 11 * factor }}>
                No transactions yet.
            </Typography>
        );
    }
    return (
        <TableContainer>
            <Table size="small" sx={{ '& th, & td': { fontSize: 11 * factor } }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Expires (UTC)</TableCell>
                        <TableCell>By</TableCell>
                        <TableCell align="center" width={48} aria-label="actions" />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[...transactions]
                        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                        .map(tx => (
                            <TableRow key={tx.transactionID} hover>
                                <TableCell>{new Date(tx.timestamp).toLocaleDateString('en-US', { timeZone: 'UTC' })}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={tx.type}
                                        size="small"
                                        color={tx.type === 'earned' ? 'success' : tx.type === 'redeemed' ? 'warning' : 'default'}
                                        sx={{ fontSize: 10 * factor }}
                                    />
                                </TableCell>
                                <TableCell>{tx.description}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, color: tx.type === 'earned' ? 'success.main' : 'inherit' }}>
                                    {tx.type === 'earned' ? '+' : '-'}{tx.amount}
                                </TableCell>
                                <TableCell>{formatDateUTC(tx.expiresAt)}</TableCell>
                                <TableCell>{tx.createdBy}</TableCell>
                                <TableCell align="center" padding="checkbox">
                                    <Tooltip title="Edit expiration">
                                        <IconButton size="small" aria-label="Edit expiration" onClick={() => onEditExpires(tx)}>
                                            <EditIcon sx={{ fontSize: 14 * factor }} />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// ── Reward bucket display ────────────────────────────────────────────────────
interface RewardBucketDisplayProps {
    bucket: RewardBucket;
    title: string;
    factor: number;
    onAddReward: () => void;
    onEditRewardExpires: (tx: RewardTransaction) => void;
}

const RewardBucketDisplay = ({ bucket, title, factor, onAddReward, onEditRewardExpires }: RewardBucketDisplayProps) => (
    <Accordion defaultExpanded sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1.5} width="100%">
                <EmojiEventsIcon color="primary" sx={{ fontSize: 18 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 12 * factor }}>
                    {title}
                </Typography>
                <Box flex={1} />
                <Stack direction="row" spacing={2} alignItems="center" mr={1}>
                    <StatChip label="Balance" value={bucket.currentBalance} color="primary" factor={factor} />
                    <StatChip label="Earned" value={bucket.lifetimeEarned} color="success" factor={factor} />
                    <StatChip label="Redeemed" value={bucket.lifetimeRedeemed} factor={factor} />
                </Stack>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={e => { e.stopPropagation(); onAddReward(); }}
                    sx={{ fontSize: 10 * factor, mr: 1, flexShrink: 0 }}
                >
                    Add Reward
                </Button>
            </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 * factor, textTransform: 'uppercase', letterSpacing: 0.5, pl: 1 }}>
                Transaction History
            </Typography>
            <Box mt={0.5}>
                <TransactionLog transactions={bucket.transactions} factor={factor} onEditExpires={onEditRewardExpires} />
            </Box>
        </AccordionDetails>
    </Accordion>
);

// ── Add Reward Dialog ─────────────────────────────────────────────────────────
type Scope = 'momentGolf' | 'tour';
const REWARD_TYPES: RewardTransactionType[] = ['earned', 'adjusted', 'redeemed', 'expired'];

interface AddRewardDialogProps {
    open: boolean;
    scope: Scope;
    tourID?: string;
    tourName?: string;
    factor: number;
    appTheme: any;
    onClose: () => void;
    onSave: (payload: Omit<AddRewardPayload, 'userID' | 'createdBy'>) => Promise<void>;
}

const AddRewardDialog = ({ open, scope, tourID, tourName, factor, appTheme, onClose, onSave }: AddRewardDialogProps) => {
    const f = factor < 1 ? factor : 1;
    const [type, setType] = useState<RewardTransactionType>('earned');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reset = () => { setType('earned'); setAmount(''); setDescription(''); setExpiresAt(''); setError(null); };
    const handleClose = () => { reset(); onClose(); };

    const handleSave = async () => {
        const raw = amount.trim();
        if (!raw || isNaN(Number(raw))) {
            setError(type === 'adjusted' ? 'Enter a valid numeric amount.' : 'Enter a valid amount greater than 0.');
            return;
        }
        const num = Number(raw);
        if (type === 'adjusted') {
            if (!Number.isInteger(num) || num === 0) {
                setError('Adjustments must be a non-zero whole number (negative reduces balance).');
                return;
            }
        } else if (!Number.isInteger(num) || num <= 0) {
            setError('Enter a valid amount greater than 0.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onSave({
                scope,
                tourID: scope === 'tour' ? tourID : undefined,
                tourName: scope === 'tour' ? tourName : undefined,
                type,
                amount: num,
                description,
                expiresAt: expiresAt || undefined,
            });
            reset();
        } catch {
            setError('Failed to add reward. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const title = scope === 'momentGolf' ? 'Moment Golf' : (tourName || 'Series');

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: 14 * f }}>Add Reward — {title}</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} sx={{ mt: 2, mb: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <StyledAutocompleteInput
                        label="Type"
                        factor={f}
                        appTheme={appTheme}
                        options={REWARD_TYPES}
                        value={type}
                        getOptionLabel={(o: string) => o.charAt(0).toUpperCase() + o.slice(1)}
                        isOptionEqualToValue={(o: string, v: string) => o === v}
                        onChange={(_: any, val: RewardTransactionType | null) => { if (val) setType(val); }}
                        disableClearable
                    />
                    <StyledTextboxInput
                        label="Amount"
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                        type="number"
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                        inputProps={type === 'adjusted' ? { step: 1 } : { min: 1, step: 1 }}
                        helperText={type === 'adjusted' ? 'Use negative values to reduce balance' : undefined}
                    />
                    <StyledTextboxInput
                        label="Description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                    />
                    <StyledTextboxInput
                        label="Expiry Date (optional)"
                        value={expiresAt}
                        onChange={e => setExpiresAt(e.target.value)}
                        type="date"
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving} color="inherit">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} variant="contained">
                    {saving ? <CircularProgress size={16} color="inherit" /> : 'Add Reward'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Edit reward expiration ────────────────────────────────────────────────────
interface EditRewardExpiresDialogProps {
    open: boolean;
    tx: RewardTransaction | null;
    scope: Scope;
    tourID?: string;
    factor: number;
    appTheme: any;
    onClose: () => void;
    onSave: (payload: Omit<UpdateRewardExpiresPayload, 'userID'>) => Promise<void>;
}

const EditRewardExpiresDialog = ({ open, tx, scope, tourID, factor, appTheme, onClose, onSave }: EditRewardExpiresDialogProps) => {
    const f = factor < 1 ? factor : 1;
    const [dateStr, setDateStr] = useState('');
    const [noExpiry, setNoExpiry] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && tx) {
            const has = !!tx.expiresAt;
            setNoExpiry(!has);
            setDateStr(has ? toUTCDateInputValue(tx.expiresAt) : '');
            setError(null);
        }
    }, [open, tx?.transactionID, tx?.expiresAt, tx]);

    const handleClose = () => {
        setError(null);
        onClose();
    };

    const handleSave = async () => {
        if (!tx) return;
        if (!noExpiry && !dateStr.trim()) {
            setError('Choose an expiry date or enable "No expiration".');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onSave({
                scope,
                tourID: scope === 'tour' ? tourID : undefined,
                transactionID: tx.transactionID,
                expiresAt: noExpiry ? null : dateStr.trim(),
            });
            handleClose();
        } catch {
            setError('Failed to update expiration. Try again.');
        } finally {
            setSaving(false);
        }
    };

    const descPreview = tx?.description?.trim() ? tx.description.trim().slice(0, 48) + (tx.description.trim().length > 48 ? '…' : '') : tx?.transactionID;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: 14 * f }}>Edit reward expiration</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} sx={{ mt: 1, mb: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {tx && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 * f }}>
                            {descPreview}
                        </Typography>
                    )}
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={noExpiry}
                                onChange={e => {
                                    const c = e.target.checked;
                                    setNoExpiry(c);
                                    if (c) setDateStr('');
                                }}
                            />
                        }
                        label={<Typography sx={{ fontSize: 12 * f }}>No expiration</Typography>}
                    />
                    <StyledTextboxInput
                        label="Expiry date (UTC date)"
                        value={dateStr}
                        onChange={e => setDateStr(e.target.value)}
                        type="date"
                        factor={f}
                        appTheme={appTheme}
                        fullWidth
                        disabled={noExpiry}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving} color="inherit">Cancel</Button>
                <Button onClick={handleSave} disabled={saving || !tx} variant="contained">
                    {saving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const RewardsSection = (_props: RewardsSectionProps) => {
    const { factor, appTheme, selectedUser } = useTBContext();
    const api = CreateApi();
    const auth = useAuth();

    const [rewards, setRewards] = useState<UserRewards | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rewardDialog, setRewardDialog] = useState<{ open: boolean; scope: Scope; tourID?: string; tourName?: string }>({ open: false, scope: 'momentGolf' });
    const [expiresEdit, setExpiresEdit] = useState<{ open: boolean; tx: RewardTransaction | null; scope: Scope; tourID?: string }>(
        { open: false, tx: null, scope: 'momentGolf' });

    const f = factor < 1 ? factor : 1;

    const fetchRewards = useCallback(() => {
        if (!selectedUser) return;
        setLoading(true);
        setError(null);
        api.get(`/um/GetRewards?userID=${selectedUser.userID}`)
            .then(res => setRewards(res.data))
            .catch(() => setError('Failed to load rewards.'))
            .finally(() => setLoading(false));
    }, [selectedUser?.userID]);

    useEffect(() => { fetchRewards(); }, [fetchRewards]);

    const handleAddReward = useCallback(async (payload: Omit<AddRewardPayload, 'userID' | 'createdBy'>) => {
        await api.post('/um/AddReward', {
            ...payload,
            userID: selectedUser!.userID,
            createdBy: auth.user?.profile?.sub ?? 'admin',
        });
        fetchRewards();
    }, [api, auth, fetchRewards, selectedUser]);

    const handleUpdateRewardExpires = useCallback(async (payload: Omit<UpdateRewardExpiresPayload, 'userID'>) => {
        await api.put('/um/UpdateRewardExpires', {
            ...payload,
            userID: selectedUser!.userID,
        });
        fetchRewards();
    }, [api, fetchRewards, selectedUser]);

    if (loading) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', minHeight: 0 }}>
                <CircularProgress />
            </Stack>
        );
    }

    const mgBucket: RewardBucket = rewards?.momentGolf ?? EMPTY_MG_BUCKET;

    // Merge profile tours with loaded reward data — always show a bucket for every
    // series on the user's profile, even when no transactions exist yet.
    const profileTours = selectedUser?.tours ?? [];
    const tourBuckets: TourRewardBucket[] = profileTours.map(t => {
        const existing = (rewards?.tour ?? []).find(b => b.tourID === t.tourID);
        return existing ?? {
            tourID: t.tourID,
            tourName: t.tourName,
            currentBalance: 0,
            lifetimeEarned: 0,
            lifetimeRedeemed: 0,
            transactions: [],
        };
    });

    return (
        <Box
            sx={{
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                px: 2,
                py: 1.5,
                boxSizing: 'border-box',
            }}
        >
            <Box sx={{ flexShrink: 0, mb: 1.5 }}>
                <Typography variant="subtitle1" color="primary.main" sx={{ fontWeight: 600, fontSize: 14 * f }}>
                    Rewards
                </Typography>
                {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {/* ── Moment Golf Rewards ──────────────────────────────────────── */}
                <Typography
                    variant="caption"
                    color="primary.main"
                    sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 * f }}
                >
                    Moment Golf Rewards
                </Typography>
                <Box mt={0.75} mb={2.5}>
                    <RewardBucketDisplay
                        bucket={mgBucket}
                        title="Moment Golf Platform"
                        factor={f}
                        onAddReward={() => setRewardDialog({ open: true, scope: 'momentGolf' })}
                        onEditRewardExpires={tx => setExpiresEdit({ open: true, tx, scope: 'momentGolf' })}
                    />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* ── Series / Tour Rewards ─────────────────────────────────────── */}
                <Typography
                    variant="caption"
                    color="primary.main"
                    sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 * f }}
                >
                    Series / Tour Rewards
                </Typography>
                <Box mt={0.75} sx={{ pb: 0.5 }}>
                    {tourBuckets.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 * f }}>
                                No series associations on this account. Add a series from the User Profile tab.
                            </Typography>
                        </Paper>
                    ) : (
                        tourBuckets.map(bucket => (
                            <RewardBucketDisplay
                                key={bucket.tourID}
                                bucket={bucket}
                                title={bucket.tourName || bucket.tourID}
                                factor={f}
                                onAddReward={() => setRewardDialog({ open: true, scope: 'tour', tourID: bucket.tourID, tourName: bucket.tourName })}
                                onEditRewardExpires={tx => setExpiresEdit({ open: true, tx, scope: 'tour', tourID: bucket.tourID })}
                            />
                        ))
                    )}
                </Box>
            </Box>

            <AddRewardDialog
                open={rewardDialog.open}
                scope={rewardDialog.scope}
                tourID={rewardDialog.tourID}
                tourName={rewardDialog.tourName}
                factor={f}
                appTheme={appTheme}
                onClose={() => setRewardDialog(d => ({ ...d, open: false }))}
                onSave={payload => handleAddReward(payload)}
            />
            <EditRewardExpiresDialog
                open={expiresEdit.open}
                tx={expiresEdit.tx}
                scope={expiresEdit.scope}
                tourID={expiresEdit.tourID}
                factor={f}
                appTheme={appTheme}
                onClose={() => setExpiresEdit(d => ({ ...d, open: false }))}
                onSave={handleUpdateRewardExpires}
            />
        </Box>
    );
};
